import React, { useEffect, useMemo, useState } from 'react';
import { Account } from '../types/Account';
import { useApi } from '../services/apiService';
import { isRemoved, isStale } from '../utils/accountStatus';
import AccountCard, { AccountAction } from './AccountCard';
import AddAccountDialog from './AddAccountDialog';
import BalanceDialog from './BalanceDialog';
import ConfirmDialog from './ConfirmDialog';
import Icon from './Icon';

interface AccountsPanelProps {
  accounts: Account[];
  // Called after any change so totals and allocation can refetch.
  onChanged: () => void;
}

type OpenDialog =
  | { kind: 'add' }
  | { kind: 'remove' | 'convert' | 'updateBalance'; account: Account };

const AccountsPanel: React.FC<AccountsPanelProps> = ({
  accounts,
  onChanged,
}) => {
  const { setAccountRemoved } = useApi();
  const [showStaleOnly, setShowStaleOnly] = useState(false);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [updatingAccountId, setUpdatingAccountId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const { activeAccounts, staleAccounts, removedAccounts, currencies } =
    useMemo(() => {
      const active = accounts.filter((account) => !isRemoved(account));
      return {
        activeAccounts: active,
        staleAccounts: active.filter((account) => isStale(account)),
        removedAccounts: accounts.filter(isRemoved),
        currencies: Array.from(
          new Set(['NZD', ...accounts.map((a) => a.balance.currency)]),
        ),
      };
    }, [accounts]);

  // Nothing left to filter once every stale account is dealt with.
  useEffect(() => {
    if (staleAccounts.length === 0) setShowStaleOnly(false);
  }, [staleAccounts.length]);

  const visibleAccounts = showStaleOnly ? staleAccounts : activeAccounts;

  const closeDialog = () => setDialog(null);

  const handleSaved = () => {
    setDialog(null);
    onChanged();
  };

  const updateRemoved = async (account: Account, removed: boolean) => {
    setUpdatingAccountId(account._id);
    setError(null);
    try {
      await setAccountRemoved(account._id, removed);
      onChanged();
      return true;
    } catch (err) {
      return false;
    } finally {
      setUpdatingAccountId(null);
    }
  };

  // Restoring is harmless so it's immediate; everything else opens a dialog.
  const handleAction = async (account: Account, action: AccountAction) => {
    if (action === 'restore') {
      if (!(await updateRemoved(account, false))) {
        setError(`Could not restore ${account.name}. Please try again.`);
      }
      return;
    }
    setError(null);
    setDialog({ kind: action, account });
  };

  const handleConfirmRemove = async () => {
    if (dialog?.kind !== 'remove') return;
    if (await updateRemoved(dialog.account, true)) {
      setRemoveError(null);
      setDialog(null);
    } else {
      setRemoveError('Could not remove the account. Please try again.');
    }
  };

  const renderCard = (account: Account) => (
    <AccountCard
      key={account._id}
      account={account}
      onAction={handleAction}
      updating={updatingAccountId === account._id}
    />
  );

  return (
    <section
      id="accounts"
      className="card dashboard-col-narrow"
      aria-labelledby="accounts-title"
    >
      <div className="section-header">
        <h2 id="accounts-title" className="card-title">
          Accounts
        </h2>
        <div className="account-header-meta">
          {staleAccounts.length > 0 && (
            <button
              className="account-filter"
              aria-pressed={showStaleOnly}
              onClick={() => setShowStaleOnly((value) => !value)}
              title="Accounts with no balance update in the last 24 hours"
            >
              {staleAccounts.length} not updated
            </button>
          )}
          <span className="muted">{activeAccounts.length} connected</span>
          <button
            className="account-add"
            onClick={() => setDialog({ kind: 'add' })}
            aria-label="Add manual account"
            title="Add manual account"
          >
            <Icon name="plus" size={16} />
          </button>
        </div>
      </div>
      {error && <p className="error account-panel-error">{error}</p>}
      <ul className="account-list" tabIndex={0} aria-label="Connected accounts">
        {visibleAccounts.map(renderCard)}
        {!showStaleOnly && removedAccounts.length > 0 && (
          <>
            <li className="account-group-label" aria-hidden="true">
              Removed
            </li>
            {removedAccounts.map(renderCard)}
          </>
        )}
      </ul>

      {dialog?.kind === 'add' && (
        <AddAccountDialog
          currencies={currencies}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}
      {(dialog?.kind === 'convert' || dialog?.kind === 'updateBalance') && (
        <BalanceDialog
          account={dialog.account}
          mode={dialog.kind === 'convert' ? 'convert' : 'update'}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}
      {dialog?.kind === 'remove' && (
        <ConfirmDialog
          open
          title="Mark account as removed?"
          confirmLabel="Mark removed"
          destructive
          busy={updatingAccountId === dialog.account._id}
          onConfirm={handleConfirmRemove}
          onCancel={() => {
            setRemoveError(null);
            closeDialog();
          }}
        >
          <p>
            <strong>{dialog.account.name}</strong>
            {dialog.account.connection.name
              ? ` · ${dialog.account.connection.name}`
              : ''}{' '}
            will be excluded from your total balance and asset allocation.
          </p>
          <p className="muted">
            Its history is kept, and you can restore it at any time from the
            Removed list.
          </p>
          {removeError && <p className="error">{removeError}</p>}
        </ConfirmDialog>
      )}
    </section>
  );
};

export default AccountsPanel;
